import React, { forwardRef } from 'react';
import { Grow, Tooltip as MuiTooltip, Box as MuiBox } from '@mui/material';
import { ChatMessage, WorkProduct } from '../types/index';

// ============================================================================
// TYPES AND INTERFACES
// ============================================================================

export interface WorkProductTextProps {
  workProduct: WorkProduct;
  onAddToEditable?: (workProduct: WorkProduct) => void;
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

// Updated to ensure ALL disclosure widgets start open by default
export const getDefaultExpandedAccordions = (): Set<string> => new Set([
  // Discovery phase disclosure widgets
  'discovery-select',
  // Design phase disclosure widgets
  'design-select',
  // Code phase disclosure widgets
  'code-select',
  // QA phase disclosure widgets
  'qa-select',
  // Deploy phase disclosure widgets
  'deploy-select'
]);

export const loadExpandedAccordions = (): Set<string> => {
  try {
    const saved = localStorage.getItem('expandedAccordions');
    if (saved) {
      const parsed = JSON.parse(saved);
      return new Set(parsed);
    }
  } catch (error) {
    console.warn('Failed to load expanded accordions from localStorage:', error);
  }
  
  // Return default expanded state (all open) for first-time users
  return getDefaultExpandedAccordions();
};

export const loadChatPanelVisibility = (): boolean => {
  try {
    const saved = localStorage.getItem('aiChatPanelVisible');
    return saved ? JSON.parse(saved) : false;
  } catch (error) {
    console.warn('Failed to load chat panel visibility from localStorage:', error);
    return false;
  }
};

export const loadChatMessages = (): ChatMessage[] => {
  try {
    const saved = localStorage.getItem('aiChatMessages');
    if (saved) {
      const parsed = JSON.parse(saved);
      return parsed.map((msg: any) => ({
        ...msg,
        timestamp: new Date(msg.timestamp)
      }));
    }
  } catch (error) {
    console.warn('Failed to load chat messages from localStorage:', error);
  }
  
  return [
    { 
      id: 'ai-chat-1', 
      type: 'ai' as const, 
      content: "Hello! I'm your AI assistant. I can help you with work products, provide insights about your products, or answer questions about the SDLC process. How can I assist you today?",
      timestamp: new Date() 
    }
  ];
};

// ============================================================================
// COMPONENTS
// ============================================================================

export const GrowTransition = React.forwardRef<HTMLDivElement, any>((props, ref) => (
  <Grow ref={ref} {...props} timeout={300}>
    {props.children}
  </Grow>
));
GrowTransition.displayName = 'GrowTransition';

export const WorkProductText: React.FC<WorkProductTextProps> = ({
  workProduct,
  onAddToEditable
}) => {
  const handleClick = () => {
    if (workProduct.state === 'available-to-add') {
      onAddToEditable?.(workProduct);
    }
  };

  return (
    <MuiTooltip
      title={workProduct.description || ''}
      placement="left"
      arrow
      slotProps={{
        tooltip: {
          sx: {
            bgcolor: '#000000',
            color: '#ffffff',
            maxWidth: 300,
            fontSize: '0.75rem',
            '& .MuiTooltip-arrow': {
              color: '#000000',
            },
          },
        },
      }}
    >
      <MuiBox
        onClick={handleClick}
        sx={{
          fontSize: '0.875rem',
          py: 1,
          cursor: 'pointer',
          color: 'text.secondary',
          transition: 'color 0.2s',
          '&:hover': {
            color: 'text.primary'
          }
        }}
      >
        {workProduct.name}
      </MuiBox>
    </MuiTooltip>
  );
};

// ============================================================================
// MOCK DATA
// ============================================================================

export const getFeatureMockData = (featureName: string) => {
  const mockDescriptions = {
    'Smart Inventory Tracking': 'Advanced inventory monitoring system that uses AI-powered receipt scanning and smart device integration to provide real-time visibility into fridge contents, preventing overbuying and reducing food waste.',
    'Expiration Management': 'Intelligent expiration tracking system that provides personalized alerts based on user consumption patterns and clarifies confusing expiration labels to help users make informed decisions about food safety.',
    'Meal Planning': 'Waste-focused meal planning tool that prioritizes using existing inventory items to create personalized meal plans, reducing food waste while encouraging creative use of available ingredients.',
    'Sustainable Shopping': 'Eco-conscious shopping platform that connects users with imperfect or surplus produce options, promotes sustainable shopping habits, and provides access to local initiatives supporting food waste reduction.',
  };
  
  const mockGoals = {
    'Smart Inventory Tracking': ['Reduce food waste', 'Prevent duplicate purchases', 'Increase inventory visibility'],
    'Expiration Management': ['Clarify expiration dates', 'Reduce premature food disposal', 'Improve food safety awareness'],
    'Meal Planning': ['Utilize existing inventory', 'Reduce food waste', 'Encourage creative cooking'],
    'Sustainable Shopping': ['Promote sustainable practices', 'Support local initiatives', 'Reduce environmental impact'],
  };
  
  const mockMetrics = {
    'Smart Inventory Tracking': ['Inventory accuracy rate', 'Duplicate purchase reduction', 'User engagement with tracking'],
    'Expiration Management': ['Food waste reduction', 'Alert response rate', 'User satisfaction with clarity'],
    'Meal Planning': ['Inventory utilization rate', 'Meal plan completion', 'Food waste reduction'],
    'Sustainable Shopping': ['Sustainable purchase rate', 'User retention', 'Environmental impact score'],
  };
  
  return {
    description: mockDescriptions[featureName as keyof typeof mockDescriptions] || 'A comprehensive feature designed to enhance user experience and deliver measurable value through modern architecture and user-centric design.',
    goals: mockGoals[featureName as keyof typeof mockGoals] || ['Improve user experience', 'Increase operational efficiency', 'Enhance system performance'],
    metrics: mockMetrics[featureName as keyof typeof mockMetrics] || ['User satisfaction score', 'Performance improvement', 'Task completion time']
  };
};

export const getSDLCIconStyle = (): React.CSSProperties & Record<string, string> => ({
  width: '24px',
  height: '24px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '--stroke-0': '#103699'
});